import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import { PAUTWeldScanItem } from '../../../utils/models';
import { getSectionOptions, getWeldScanColumns } from '../../../utils/data';

import AutTable from '../../blocks/aut-grid-table';
import Form from '../../common/form';
import PAUTWeldScanItemForm from './pautWeldScanItemForm';

class PAUTWeldScanForm extends Form {
	state = {
		data: initialData,
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		shearWaveSrNo: Joi.string()
			.min(3)
			.required()
			.label('Shear Wave Equipment Sr.No'),
		calibrationExpiryDate: Joi.date()
			.required()
			.label('Calibration Certificate Expiry Date'),
		probeSrNo: Joi.string().min(3).required().label('Probe Sr No.'),
		probeFrequency: Joi.string().min(3).required().label('Probe Frequency'),
		weldCondition: Joi.string().min(3).required().label('Weld Condition'),
		materialType: Joi.string().min(3).required().label('Material Type'),
		weldType: Joi.string().min(3).required().label('Weld Type'),
		scanning: Joi.string()
			.min(3)
			.required()
			.label('Scanning (Internal / External)'),
		couplantType: Joi.string().min(3).required().label('Couplant Type'),
		modalName: Joi.string().min(3).required().label('Modal Name'),
		calibrationBlkType: Joi.string()
			.min(3)
			.required()
			.label('Calibration Block Type'),
		probeType: Joi.string().min(3).required().label('Probe Type'),
		probeAngle: Joi.string().min(3).required().label('Probe Angle'),
		probeDia: Joi.string().min(3).required().label('Probe Dia'),
		bevelAngle: Joi.number().positive().required().label('Bevel Angle (A)'),
		weldThickness: Joi.number()
			.positive()
			.required()
			.label('Weld Thickness (B)'),
		rootGap: Joi.number().positive().required().label('Root Gap (C)'),
		rootLength: Joi.number().positive().required().label('Root Gap (D)'),
		singleOrDouble: Joi.string().required().label('Single V or Double V'),

		data: Joi.array()
			.min(1)
			.items(
				Joi.object({
					srNo: Joi.number().positive().required().label('SR No'),
					weldNo: Joi.string().required().label('Weld Number'),
					jointDetail: Joi.string().min(3).required().label('Joint Detail'),
					weldDrawing: Joi.array().min(1).required().label('Weld Drawing'),
					metrics: Joi.string()
						.min(10)
						.required()
						.label('Type and Length of Defect'),
					result: Joi.string().min(3).required().label('Result'),
				})
			)
			.required()
			.label('Scan Details'),
	};

	static propTypes = {
		onProceed: PropTypes.func,
	};

	doSubmit = (): void => {
		const props: any = this.props;
		props.onProceed(this.state.data);

		this.setState({ data: initialData });
	};

	onAddItem = (item: PAUTWeldScanItem) => {
		const data: any = { ...this.state.data };
		data.data = [...data.data];
		data.data.push({ ...item, srNo: this.state.data.data.length + 1 });

		this.setState({ data });
	};

	onDeleteItem = (item: PAUTWeldScanItem): void => {
		const data = { ...this.state.data };
		data.data = data.data.filter((i: PAUTWeldScanItem) => i.srNo !== item.srNo);

		this.setState({ data });
	};

	render() {
		return (
			<React.Fragment>
				<PAUTWeldScanItemForm onAdd={this.onAddItem} />

				<div className="divider my-4"></div>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'shearWaveSrNo',
								label: 'Shear Wave Equipment Sr.No',
								placeholder: 'Enter Shear Wave Equipment Sr.No',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'calibrationExpiryDate',
								label: 'Calibration Certificate Expiry Date',
								placeholderText: 'Select Calibration Certificate Expiry Date',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'probeSrNo',
								label: 'Probe Sr No.',
								placeholder: 'e.g. Enter Probe Sr No.',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'probeFrequency',
								label: 'Probe Frequency',
								placeholder: 'Enter Probe Frequency',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								name: 'materialType',
								type: 'text',
								label: 'Material Type',
								placeholder: 'Enter Material Type',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								name: 'weldType',
								type: 'text',
								label: 'Weld Type',
								placeholder: 'Enter Weld Type',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								name: 'weldCondition',
								type: 'text',
								label: 'Weld Condition',
								placeholder: 'Enter Weld Condition',
							})}
						</div>

						<div className="col">
							{this.renderSelect({
								name: 'scanning',
								label: 'Scanning (Internal / External)',
								placeholder: 'Enter Scanning (Internal / External)',
								options: getSectionOptions(),
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'probeAngle',
								label: 'Probe Angle',
								placeholder: 'Enter Probe Angle',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'probeDia',
								label: 'Probe Dia',
								placeholder: 'Enter Probe Dia',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'calibrationBlkType',
								label: 'Calibration Block Type',
								placeholder: 'Enter Calibration Block Type',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'probeType',
								label: 'Probe Type',
								placeholder: 'Enter Probe Type',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'couplantType',
								label: 'Couplant Type',
								placeholder: 'Enter Couplant Type',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'modalName',
								label: 'Modal Name',
								placeholder: 'Enter Modal Name',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'bevelAngle',
								label: 'Bevel Angle (A)',
								placeholder: 'Enter Bevel Angle (A)',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'weldThickness',
								label: 'Weld Thickness (B)',
								placeholder: 'Enter Weld Thickness (B)',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'rootGap',
								label: 'Root Gap (C)',
								placeholder: 'Enter Root Gap (C)',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'rootLength',
								label: 'Root Length (D)',
								placeholder: 'Enter Root Length (D)',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'singleOrDouble',
								label: 'Single V or Double V',
								placeholder: 'Enter Single V or Double V',
							})}
						</div>
					</div>

					{this.renderButton('Submit')}
				</form>

				<div className="divider my-4"></div>

				<AutTable
					tableColumns={getWeldScanColumns()}
					data={this.state.data.data}
					onDeleteItem={this.onDeleteItem}
				/>
			</React.Fragment>
		);
	}
}

export default PAUTWeldScanForm;

const initialData = {
	shearWaveSrNo: '',
	calibrationExpiryDate: new Date(),
	probeSrNo: '',
	probeFrequency: '',
	weldCondition: '',
	materialType: '',
	weldType: '',
	scanning: '',
	couplantType: '',
	calibrationBlkType: '',
	probeType: '',
	modalName: '',
	probeAngle: '',
	probeDia: '',
	bevelAngle: '',
	weldThickness: '',
	rootGap: '',
	rootLength: '',
	singleOrDouble: '',
	data: [],
};
